import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Header } from '../../components/shared/header/header';
import { LeadsTable, LeadColumn, LeadRow, FilterConfig, ServerSidePagination } from '../../components/shared/leads-table/leads-table';
import { LeadsFilaItem, LeadsFilaRequest } from '../../interfaces/leads-fila.interface';
import { catchError, finalize, of, Subscription } from 'rxjs';
import { LeadsFilaService } from '../../services/leads-fila.service';
import { SelectedDnService } from '../../services/selected-dn.service';
import { FilterFieldsUtil } from '../../utils/filter-fields-utils';
import { availableOriginsType } from '../../components/shared/filter-fields/models/filter-field.model';
import { DashboardStoreService } from '../../services/store/dashboard.store.service';

@Component({
  selector: 'app-approved-proposals',
  standalone: true,
  imports: [CommonModule, RouterModule, Header, LeadsTable],
  templateUrl: './approved-proposals.html',
  styleUrls: ['./approved-proposals.scss']
})
export class ApprovedProposals implements OnDestroy {
  private dnSubscription?: Subscription;
  availableFilterOrigins: availableOriginsType[] = ['sempre-novo', 'ofertas-vw', 'campanha'];

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'numeroDaProposta', label: 'Número da Proposta', type: 'text' },
    { key: 'dataProposta', label: 'Data da Proposta', type: 'text' },
    { key: 'disponivelDesde', label: 'Disponibilizado Em', type: 'text' },
    { key: 'ultimoContato', label: 'Último Contato', type: 'text' },
    { key: 'retornarEm', label: 'Retornar em', type: 'text' },
    { key: 'status', label: 'Status do Lead', type: 'status' }
  ];

  filterConfig: FilterConfig = {
    searchFields: ['nome', 'numeroProposta'], // Busca por nome e número da proposta
    alphabeticalField: 'NM_NOME', // Ordenação alfabética por nome
    filterFields: {
      renewal: 'renovaMais', // Apenas renewal disponível
      origin: 'origem'
    },
    dateField: 'dataProposta' // Filtro de data por dataProposta
  };

  tableData: LeadRow[] = [];
  loading: boolean = false;
  loadingMessage: string = 'Carregando dados...';
  errorMessage: string = '';
  emptyMessage: string = 'Nenhum lead encontrado para o tipo de campanha informada.';
  serverSidePagination?: ServerSidePagination;

  // TODO: Obter do contexto do usuário logado quando disponível
  private readonly tipoCampanha = 2; // Simulações Online

  // Propriedades para controlar os filtros
  private currentSearchText: string | undefined = undefined;
  private currentFilters: string[] = [];
  private currentDateRange: { start: string, end: string } = { start: '', end: '' };
  private currentSortField: string = 'DT_PROPOSTA';
  private currentSortOrder?: 'ASC' | 'DESC' = 'DESC';

  constructor(
    private leadsFilaService: LeadsFilaService,
    private router: Router,
    private selectedDnService: SelectedDnService,
    private readonly dashboardStoreService: DashboardStoreService,
  ) { }

  ngOnInit(): void {

    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.currentDateRange = {
      start: this.formatDateForInput(threeMonthsAgo),
      end: this.formatDateForInput(today)
    };

    this.loadData();

    // Subscreve às mudanças do DN
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn) {
        // Resetar para primeira página ao mudar DN
        this.loadData(1, this.serverSidePagination?.pageSize || 20);
      }
    });
  }

  ngOnDestroy(): void {
    // Limpa a subscription para evitar memory leaks
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }


  loadData(
    page: number = 1,
    pageSize: number = 20
  ): void {
    this.loading = true;
    this.loadingMessage = 'Carregando propostas aprovadas...';
    this.errorMessage = '';

    const request: LeadsFilaRequest = {
      tipoCampanha: this.tipoCampanha,
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      preAprovado: FilterFieldsUtil.getPreAprovadoFromFilters(this.currentFilters),
      renovaMais: FilterFieldsUtil.getRenovaMaisFromFilters(this.currentFilters),
      origem: FilterFieldsUtil.getOrigemFromFilters(this.currentFilters),
      nome: this.currentSearchText,
      dataInicio: this.parseDate(this.dashboardStoreService?.startDate() || ''),
      dataFim: this.parseDate(this.dashboardStoreService?.endDate() || ''),
      page: page,
      pageSize: pageSize,
      sortField: this.currentSortField,
      sortOrder: this.currentSortOrder
    };

    this.leadsFilaService.getLeadsFila(request).pipe(
      catchError((error) => {
        console.error('Erro ao carregar leads:', error);

        if (error.status === 401) {
          this.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        } else if (error.status === 204) {
          this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';
          this.tableData = [];
          this.loading = false;
        } else {
          this.errorMessage = 'Erro ao carregar dados. Tente novamente.';
          this.loading = false;
        }

        return of(null);
      })
    ).subscribe((response) => {
      if (response && response.data && response.data.length > 0) {
        const mappedData = this.mapApiDataToTableData(response.data);
        this.tableData = [...mappedData]; // Inicialmente mostra todos os dados
        this.serverSidePagination = {
          totalRecords: response.pagination.totalRecords,
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          from: response.pagination.from,
          to: response.pagination.to
        };
      } else {
        this.emptyMessage = response?.message || 'Nenhum lead encontrado para o tipo de campanha informada.';
        this.tableData = [];
      }
      this.loading = false;
    });
  }

  private mapApiDataToTableData(apiData: LeadsFilaItem[]): LeadRow[] {
    return apiData.map((item) => {
      return {
        idLead: item.idLead,
        nome: item.nomeCliente || '-',
        numeroDaProposta: item.numeroDaProposta || '-',
        dataProposta: (item.dataProposta && item.dataProposta !== 'null' && item.dataProposta !== 'undefined') ? item.dataProposta : '-',
        disponivelDesde: (item.disponivelDesde && item.disponivelDesde !== 'null' && item.disponivelDesde !== 'undefined') ? item.disponivelDesde : '-',
        ultimoContato: (item.ultimoContato && item.ultimoContato !== 'null' && item.ultimoContato !== 'undefined') ? item.ultimoContato : '-',
        retornarEm: (item.retornarEm && item.retornarEm !== 'null' && item.retornarEm !== 'undefined') ? item.retornarEm : '-',
        status: item.statusLead || '-',
        renewal: this.mapRenovaMais(item)
      };
    });
  }

  private mapRenovaMais(item: LeadsFilaItem): string {
    return item.renovaMais === true ? 'renewalBlue' : 'renewalGrey';
  }

  // Métodos para ordenação server-side
  onSortChange(sort: { field: string, order: 'ASC' | 'DESC' }): void {
    this.currentSortField = sort.field;
    this.currentSortOrder = sort.order;
    this.loadData(1, this.serverSidePagination?.pageSize || 20);
  }

  // Métodos para filtros client-side
  onSearchChange(searchText: string): void {
    this.currentSearchText = searchText;
    this.loadData();
  }

  onFilterChange(filters: string[]): void {
    this.currentFilters = filters;
    this.loadData();
  }

  onDateRangeChange(dateRange: { start: string, end: string }): void {
    this.currentDateRange = dateRange;
    this.loadData();
  }

  onClearAllFilters(): void {
    this.currentSearchText = '';
    this.currentFilters = [];
    this.currentDateRange = { start: '', end: '' };
    this.currentSortField = 'DT_PROPOSTA';
    this.currentSortOrder = 'DESC';

    // Restaura os dados originais
    this.loadData();
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString) return undefined;
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    if (dateString.includes('-')) {
      return new Date(dateString);
    }
    return undefined;
  }

  onPageSizeChange(newPageSize: number): void {
    this.loadData(1, newPageSize);
  }

  onPageChange(newPage: number): void {
    if (this.serverSidePagination) {
      this.loadData(newPage, this.serverSidePagination.pageSize);
    }
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      // Usar nova rota com parâmetro na URL
      this.router.navigate([`/leads/${lead.idLead}/detalhes`]);
    }
  }
}
